import {
    Button,
    ButtonStyles,
    Dropdown,
    EmptyState,
    EmptyStateIcons,
    Icons,
    TextInput
} from '@silverfort/sf-components';
import { ProtocolsSelector } from './protocolsSelector';
import ResourcesList from './ResourcesList';
import { useEffect, useState } from 'react';
import {
    AD_ENTITY_TYPES,
    JIT_SUPPORTED_PROTOCOLS,
    PRIVILEGED_DEFAULT_SERVICES,
    HTTP_OPTIONS,
    PRIVILEGE_ACCOUNTS_ALL_SERVICES,
    SEARCH_MIN_CHARS,
    TIER_PART_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { SCOPE_SHOW_SERVICES, TIER_SCOPES, DEFAULT_PROTOCOLS_AD, DEFAULT_PROTOCOLS_IP } from '../TierConsts';
import ResourcePicker from '../../../../../../Controls/ResourcePicker/ResourcePicker';
import { httpGet } from '../../../../../../utils';
import { AdTypeOptions } from '../../../../../../Controls/ResourcePicker/ResourcePickerConsts';
import { useSelector } from 'react-redux';
import { useTierResourcesContext } from '../../TierResourcesContext';

const TIER_PART_TYPES_FILTER_OPTIONS = [
    { title: 'Groups', value: TIER_PART_TYPES.GROUPS },
    { title: 'OUs', value: TIER_PART_TYPES.OUS },
    { title: 'Servers', value: TIER_PART_TYPES.SERVERS },
    { title: 'IPS', value: TIER_PART_TYPES.IPS }
];

const Resources = ({ tierPart, defaultSelected, scope, isEditMode, onChange, setShouldDisableSave }) => {
    const dcs = useSelector((state) => new Set(Object.keys(state.settings.settings.DomainControllersConfigurations)));
    const [resources, setResources] = useState([]);
    const [filteredResources, setFilterResources] = useState([]);
    const [selectedResourceIndex, setSelectedResourceIndex] = useState();

    const [typeFilter, setTypeFilter] = useState();
    const [nameFilter, setNameFiler] = useState('');

    useEffect(() => {
        if (isEditMode) {
            return;
        }
        const defaultServicesWhenKerberos = (protocols, services) =>
            protocols?.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS) && (!services || services.length === 0)
                ? [...PRIVILEGED_DEFAULT_SERVICES]
                : services ?? [];

        const allResources = [];
        for (const type of [TIER_PART_TYPES.SERVERS, TIER_PART_TYPES.GROUPS, TIER_PART_TYPES.OUS]) {
            for (const resourceId in tierPart[type]) {
                const data = tierPart[type][resourceId];
                const resource = {
                    ...data,
                    type: type,
                    id: resourceId,
                    protocols: data.protocols ?? DEFAULT_PROTOCOLS_AD,
                    services: defaultServicesWhenKerberos(data.protocols ?? DEFAULT_PROTOCOLS_AD, data.services)
                };
                allResources.push(resource);
            }
        }
        for (const ip in tierPart[TIER_PART_TYPES.IPS]) {
            const ipData = tierPart[TIER_PART_TYPES.IPS][ip];
            const resource = {
                type: TIER_PART_TYPES.IPS,
                id: ip,
                services: [],
                protocols: ipData.protocols ?? DEFAULT_PROTOCOLS_IP
            };
            allResources.push(resource);
        }

        setResources(allResources);
        setFilterResources(allResources);
        if (allResources.length) {
            setSelectedResourceIndex(allResources[0]);
        }
    }, [tierPart, defaultSelected, isEditMode]);

    useEffect(() => {
        if (defaultSelected) {
            const selected = filteredResources.find(
                (resource) => resource.id === defaultSelected.id && resource.type === defaultSelected.type
            );
            if (selected) {
                setSelectedResourceIndex(selected);
            }
        }
    }, [defaultSelected]);

    useEffect(() => {
        setTypeFilter();
        setNameFiler('');
    }, [isEditMode]);

    useEffect(() => {
        const newTierPart = {
            ...tierPart,
            [TIER_PART_TYPES.SERVERS]: {},
            [TIER_PART_TYPES.OUS]: {},
            [TIER_PART_TYPES.GROUPS]: {},
            [TIER_PART_TYPES.IPS]: {}
        };

        for (const resource of resources) {
            newTierPart[resource.type][resource.id] = {
                ...resource,
                services: resource.services ?? [],
                protocols: resource.protocols ?? []
            };
        }

        onChange(newTierPart);
    }, [onChange, resources]);

    useEffect(() => {
        let filtered = [...resources];
        if (typeFilter) {
            filtered = filtered.filter((resource) => resource.type === typeFilter);
        }
        if (nameFilter) {
            filtered = filtered.filter((resource) => {
                switch (resource.type) {
                    case TIER_PART_TYPES.GROUPS:
                    case TIER_PART_TYPES.OUS:
                    case TIER_PART_TYPES.SERVERS: {
                        return resource.name.toLowerCase().includes(nameFilter);
                    }
                    case TIER_PART_TYPES.IPS: {
                        return resource.id.toLowerCase().includes(nameFilter);
                    }
                    default: {
                        return false;
                    }
                }
            });
        }
        setFilterResources(filtered);

        if (selectedResourceIndex) {
            const selectedExists = filtered.find(
                (resource) => resource.type === selectedResourceIndex.type && resource.id === selectedResourceIndex.id
            );
            if (!selectedExists) {
                if (filtered.length) {
                    setSelectedResourceIndex(filtered[0]);
                } else {
                    setSelectedResourceIndex();
                }
            }
        } else if (filtered.length) {
            setSelectedResourceIndex(filtered[0]);
        }
    }, [resources, typeFilter, nameFilter]);

    const onAddResource = (resource) => {
        const service = SCOPE_SHOW_SERVICES[scope]
            ? [...PRIVILEGED_DEFAULT_SERVICES]
            : [PRIVILEGE_ACCOUNTS_ALL_SERVICES];
        const newResource = {
            ...resource,
            domainName: resource.domain_name,
            services: service,
            protocols: DEFAULT_PROTOCOLS_AD,
            id: resource.guid
        };
        switch (resource.type) {
            case AD_ENTITY_TYPES.GROUP:
                newResource.type = TIER_PART_TYPES.GROUPS;
                break;
            case AD_ENTITY_TYPES.OU:
                newResource.type = TIER_PART_TYPES.OUS;
                break;
            case AD_ENTITY_TYPES.SERVER:
                newResource.type = TIER_PART_TYPES.SERVERS;
                break;
            default: {
                return;
            }
        }

        setResources((resources) => [...resources, newResource]);
    };

    const onAddIp = (ip) => {
        const service = SCOPE_SHOW_SERVICES[scope]
            ? [...PRIVILEGED_DEFAULT_SERVICES]
            : [PRIVILEGE_ACCOUNTS_ALL_SERVICES];
        setResources((resources) => [
            ...resources,
            { type: TIER_PART_TYPES.IPS, id: ip, services: service, protocols: DEFAULT_PROTOCOLS_IP }
        ]);
    };

    const onDeleteResource = (deletedResource) => {
        setResources((resources) =>
            resources.filter(
                (resource) => !(resource.type === deletedResource.type && resource.id === deletedResource.id)
            )
        );
    };

    const onChangeServices = (services) => {
        setResources((resources) => {
            setShouldDisableSave(false);
            const selected = resources.find(
                (resource) => resource.type === selectedResourceIndex.type && resource.id === selectedResourceIndex.id
            );
            selected.services = services;
            if (services.length === 0 && selected.protocols?.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS)) {
                selected.protocols = selected.protocols.filter((p) => p !== JIT_SUPPORTED_PROTOCOLS.KERBEROS);
            }
            return [...resources];
        });
    };

    const onChangeProtocols = (protocols) => {
        if (protocols.length === 0) {
            setShouldDisableSave(true);
        } else {
            setShouldDisableSave(false);
        }
        setResources((resources) => {
            const selected = resources.find(
                (resource) => resource.type === selectedResourceIndex.type && resource.id === selectedResourceIndex.id
            );
            selected.protocols = protocols;
            if (!protocols.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS)) {
                selected.services = [];
            } else if (!selected.services || selected.services.length === 0) {
                selected.services = [...PRIVILEGED_DEFAULT_SERVICES];
            }
            return [...resources];
        });
    };

    const onClearAll = () => {
        setResources((resources) =>
            resources.filter(
                (resource) =>
                    !filteredResources.find(
                        (filterResource) => resource.type === filterResource.type && resource.id === filterResource.id
                    )
            )
        );
    };

    return (
        <div
            className={`h-full w-[680px] rounded-lg border border-sf-gray-100 flex flex-col`}>
            <div className="h-14 w-full flex items-center px-4 border-b border-sf-gray-100 gap-2">
                <Dropdown
                    items={TIER_PART_TYPES_FILTER_OPTIONS}
                    onChange={(val) => setTypeFilter(val)}
                    removable
                    onRemove={() => setTypeFilter()}
                    selectedItems={typeFilter}
                    // size="large"
                    title="type"
                    variant="filter"
                />
                <div className="flex-1">
                    <TextInput
                        className="w-full border-0"
                        iconLeft={Icons.Search}
                        placeholder="Search added resources"
                        value={nameFilter}
                        onChange={setNameFiler}
                    />
                </div>
                <div>
                    <AddResources
                        scope={scope}
                        resources={resources}
                        onAddResource={onAddResource}
                        onAddIp={onAddIp}
                        hiddenGuids={dcs}
                        close={!isEditMode}
                        setShouldDisableSave={setShouldDisableSave}
                    />
                </div>
            </div>
            {resources.length ? (
                <div className="flex flex-1 h-full">
                    <div className="flex-1 w-full border-r border-sf-gray-100">
                        <ResourcesList
                            resources={filteredResources}
                            selected={selectedResourceIndex}
                            onSelect={setSelectedResourceIndex}
                            onDelete={onDeleteResource}
                            onClearAll={onClearAll}
                            scope={scope}
                        />
                    </div>
                    {SCOPE_SHOW_SERVICES[scope] && (
                        <div className="w-[270px] min-w-[270px]">
                            <ProtocolsSelector
                                selectedProtocols={selectedResourceIndex?.protocols ?? []}
                                onChangeProtocols={onChangeProtocols}
                                selectedServices={
                                    selectedResourceIndex?.protocols?.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS)
                                        ? selectedResourceIndex?.services ?? []
                                        : []
                                }
                                onChangeServices={onChangeServices}
                                defaultServices={PRIVILEGED_DEFAULT_SERVICES}
                                defaultProtocols={
                                    selectedResourceIndex?.type === TIER_PART_TYPES.IPS
                                        ? DEFAULT_PROTOCOLS_IP
                                        : DEFAULT_PROTOCOLS_AD
                                }
                                hideOptions={!selectedResourceIndex}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-1 h-full w-full items-center align-center justify-center">
                    <EmptyState
                        title="No resources added"
                        description="Add resources by clicking Add button"
                        icon={EmptyStateIcons.NoItems}
                    />
                </div>
            )}
        </div>
    );
};

const DEFAULT_AD_TYPES = Object.values(AdTypeOptions);

const AddResources = ({ resources, onAddResource, onAddIp, hiddenGuids, close, scope, setShouldDisableSave }) => {
    const [open, setOpen] = useState(false);
    const toggleOpen = () => {
        if (open) {
            setMatchedResources([]);
        }
        setOpen((open) => !open);
    };

    const [matchedResources, setMatchedResources] = useState([]);
    const [matchedIps, setMatchedIps] = useState([]);
    const [selectedResources, setSelectedResources] = useState([]);
    const [selectedIps, setSelectedIps] = useState([]);
    const [ipFilter, setIpFilter] = useState('');
    const [resourceFilters, setResourceFilters] = useState({});
    const [loadingResources, setLoadingResources] = useState();
    const { tiersResources } = useTierResourcesContext();

    useEffect(() => {
        if (close) {
            setOpen(false);
        }
    }, [close]);

    useEffect(() => {
        const searchResources = async () => {
            try {
                let escapedQuery = resourceFilters.searchTerm?.trim() || '';

                if (escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) {
                    setMatchedResources([]);
                } else {
                    setLoadingResources(true);
                    escapedQuery = encodeURIComponent(escapedQuery);
                    const baseSearchURL = '/users/searchEntitiesByTypes';
                    const reqUrl = `${baseSearchURL}/${escapedQuery}/${1}`;

                    const res = await httpGet(reqUrl, {
                        [HTTP_OPTIONS.REQ_OPTIONS]: {
                            params: { hideDeleted: true, entitiesTypes: resourceFilters.adTypes || DEFAULT_AD_TYPES }
                        }
                    });

                    const result = res.data?.Results || [];
                    setMatchedResources(result.filter((resource) => !hiddenGuids.has(resource.guid)));
                }
            } catch (e) {
                console.error('Failed to search resources for query ' + resourceFilters.searchTerm, e);
            } finally {
                setLoadingResources(false);
            }
        };
        searchResources();
    }, [resourceFilters]);

    useEffect(() => {
        const newSelectedIps = resources
            .filter((resource) => resource.type === TIER_PART_TYPES.IPS)
            .map((resource) => resource.id);
        setSelectedIps(newSelectedIps);
        setMatchedIps(newSelectedIps.filter((ip) => ip.startsWith(ipFilter)));
    }, [resources, ipFilter]);

    useEffect(() => {
        setSelectedResources(
            resources
                .filter((resource) => resource.type !== TIER_PART_TYPES.IPS)
                .map((resource) => {
                    return { guid: resource.id };
                })
        );
    }, [resources]);

    const handleResourceSearch = (val, type) => {
        if (type === 'ips') {
            setIpFilter(val);
        } else {
            setResourceFilters({ ...resourceFilters, searchTerm: val });
        }
    };

    const handleResourceSelect = (item, type) => {
        setShouldDisableSave(false);
        if (type === 'resources') {
            onAddResource(item);
        } else {
            setIpFilter('');
            if (!selectedIps.includes(item)) {
                onAddIp(item);
            }
        }
    };

    return (
        <div>
            <div className="relative">
                <Button
                    holdPressed={open}
                    onClick={toggleOpen}
                    text={'Add'}
                    iconLeft={Icons.Add}
                    buttonStyle={ButtonStyles.Primary}
                />
                {open && (
                    <div className="fixed z-10 top-[132px] right-[23px]">
                        <ResourcePicker
                            itemTooltipText="A server can only be assigned to one tier"
                            shouldShowItemTooltip={(item) => {
                                return (
                                    tiersResources.has(item.guid) &&
                                    !selectedResources.some((resource) => resource.guid === item.guid) &&
                                    scope === TIER_SCOPES.IN_SCOPE
                                );
                            }}
                            shouldDisableButton={(item) => {
                                return (
                                    tiersResources.has(item.guid) &&
                                    !selectedResources.some((resource) => resource.guid === item.guid) &&
                                    scope === TIER_SCOPES.IN_SCOPE
                                );
                            }}
                            loading={loadingResources}
                            title={'Resources'}
                            showAddAll={false}
                            onSelect={handleResourceSelect}
                            hideNewIPButton={false}
                            resources={matchedResources}
                            ips={matchedIps}
                            selectedResources={selectedResources}
                            selectedIPs={selectedIps}
                            onSearchChange={handleResourceSearch}
                            onAdTypeChange={(types) => setResourceFilters({ ...resourceFilters, adTypes: types })}
                            showAdTypeFilter={true}
                            onAction={() => toggleOpen()}
                            onClose={() => toggleOpen()}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Resources;
